import { isString } from 'fitify-types/src/type-guards/common'
import { CoreUser } from 'fitify-types/src/types/core/user'
import { usePromoContext } from 'fitify-ui-onboarding/src/contexts/PromoContext'
import { useSubscriptionConfig } from 'fitify-ui-onboarding/src/hooks/subscription/useSubscriptionConfig'
import { useRouter } from 'fitify-ui-onboarding/src/hooks/useRouter'
import { logEvent } from 'fitify-ui-onboarding/src/utils/analytics'
import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import type { MetaDataProps } from 'fitify-ui-onboarding/src/utils/meta-tags'
import { Suspense, useEffect, useMemo } from 'react'

import { DEFAULT_PROMO_CODE } from 'data/promo'
import { usePrintableId } from 'hooks/usePrintableId'
import type { PaymentContentOrderType } from 'sections/subscription/Content/SubscriptionContent'
import { SubscriptionPageGateway } from 'sections/subscription/SubscriptionPageGateway'
import { SubscriptionPageLoader } from 'sections/subscription/SubscriptionPageLoader'

type Props = {
  defaultPlanTitle?: string
  metaData: MetaDataProps
}

export const SubscriptionPage = ({ defaultPlanTitle, metaData }: Props) => {
  const { promoCode, isInitialized } = usePromoContext()
  const { pageConfig, subscriptionPlanConfigs } = useSubscriptionConfig({
    defaults: {
      pageConfig: { defaultPeriod: 'longest', planTitle: defaultPlanTitle },
    },
    promoCode,
  })

  const growthBook = useGrowthBook()
  const router = useRouter()

  usePrintableId()

  const gateway = useMemo(() => {
    if (!isInitialized) {
      return undefined
    }

    if (
      pageConfig.gateway === CoreUser.Subscription.Gateway.Flex &&
      subscriptionPlanConfigs.some((planConfig) =>
        isString(planConfig.flexPriceId)
      )
    ) {
      return CoreUser.Subscription.Gateway.Flex
    }
    if (
      pageConfig.gateway === CoreUser.Subscription.Gateway.Paddle &&
      subscriptionPlanConfigs.some((planConfig) =>
        isString(planConfig.paddlePriceId)
      )
    ) {
      return CoreUser.Subscription.Gateway.Paddle
    }

    if (
      pageConfig.gateway === CoreUser.Subscription.Gateway.Stripe &&
      subscriptionPlanConfigs.some((planConfig) =>
        isString(planConfig.stripePriceId)
      )
    ) {
      return CoreUser.Subscription.Gateway.Stripe
    }

    return CoreUser.Subscription.Gateway.Fastspring
  }, [isInitialized, pageConfig.gateway, subscriptionPlanConfigs])

  const paymentContentOrderType = useMemo((): PaymentContentOrderType => {
    const hasInlineCheckout =
      gateway === CoreUser.Subscription.Gateway.Paddle ||
      gateway === CoreUser.Subscription.Gateway.Stripe

    if (promoCode !== DEFAULT_PROMO_CODE || !hasInlineCheckout) {
      return 'checkout_first'
    }

    const orderType = growthBook.evalFeature(
      'payment_content_order_type'
    )?.value
    switch (orderType) {
      case 'proof_before_checkout':
      case 'benefits_first':
      case 'compact_safe_checkout_guarantee':
      case 'compact_money_guarantee':
        return orderType
      default:
        return 'checkout_first'
    }
  }, [gateway, growthBook, promoCode])

  useEffect(() => {
    if (gateway) {
      // Log paywall screen
      logEvent(`paywall`, {
        promo: promoCode,
        screen: 'primary',
        gateway,
      })
    }
  }, [gateway, promoCode])

  // Handle paywall close event, if user tries to leave subscription page
  // Listen to history popstate, if event has url set by Next.js router, compare its pathname with current pathname,
  // if they are different, it means user tries to leave the page, then log paywall_close event.
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const eventUrl = event.state?.url

      if (isString(eventUrl)) {
        const [eventPathname] = eventUrl.split('?')
        if (eventPathname === router.resolvedPathname) {
          return
        }
      }

      logEvent('paywall_close')
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [router.resolvedPathname])

  return (
    <Suspense
      fallback={
        <SubscriptionPageLoader
          metaData={metaData}
          pageConfig={pageConfig}
          paymentContentOrderType={paymentContentOrderType}
        />
      }
    >
      <SubscriptionPageGateway
        gateway={gateway}
        metaData={metaData}
        pageConfig={pageConfig}
        paymentContentOrderType={paymentContentOrderType}
        subscriptionPlanConfigs={subscriptionPlanConfigs}
      />
    </Suspense>
  )
}
